import { DragulaService } from "ng2-dragula";
import { ChangeDetectionStrategy, Component, signal, ViewChild } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import { OverlayPanel } from "primeng/overlaypanel";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from "@medsurf/flat-facades";
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { DragContainerComponent, ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-slide-wysiwyg-questions',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './questions.component.html',
    styleUrls: ['./questions.component.scss'],
    standalone: false
})
export class QuestionsComponent extends DragContainerComponent {
  @ViewChild('change_question_type_overlay', {static: false}) changeQuestionTypeOverlay: OverlayPanel;
  
  isSubmitted = signal(false);
  #handled = false;
  question: FlatModels.QuestionEntityModels.QuestionEntityType | undefined;
  questionTypeForm = new FormGroup(
    {
      type: new FormControl(''),
      required: new FormControl(false),
      placeholder: new FormControl(''),
      numberingType: new FormControl(''),
      content: new FormControl(''),
      explanation: new FormControl('')
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.QuestionEntityModels.QuestionSchema.pick({
          type: true,
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService,
              dragulaService: DragulaService) {
    super(dragulaService);
  }
  
  //<editor-fold desc="Question">

  onQuestionTypeEdit(event: MouseEvent,
                     question: FlatModels.QuestionEntityModels.QuestionEntityType) {
    this.#handled = true;
    if (this.changeQuestionTypeOverlay.overlayVisible) {
      this.changeQuestionTypeOverlay.hide();
      this.question = undefined;
    }
    setTimeout(() => {
      this.question = structuredClone(question);
      this.questionTypeForm.reset(this.question);
      this.changeQuestionTypeOverlay.show(event, event.target);
      this.isSubmitted.set(false);
    }, 200);
  }

  onQuestionTypeSubmit() {
    if (this.isSubmitted() || this.questionTypeForm.invalid || !this.question) {
      return;
    }
    this.isSubmitted.set(true);
    
    // Update Question Type
    this.slideAuthorFacade.requestChangeQuestionType(this.question, this.questionTypeForm.value['type'] as FlatModels.QuestionEntityModels.QuestionType).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.questionTypeForm);
        this.isSubmitted.set(false);
      },
      next: () => {
        this.isSubmitted.set(false);
        this.changeQuestionTypeOverlay.hide();
        this.question = undefined;
      }
    });
  }

  trackBy(index: number,
          item: FlatModels.QuestionEntityModels.QuestionEntityType) {
    return item.id + item.order || index;
  }

  onQuestionExpand(questionId: string,
                   currentQuestionId: string) {
    if (this.#handled) {
      this.#handled = false;
      return;
    }
    this.slideAuthorFacade.requestSetQuestionId(questionId === currentQuestionId ? null : questionId, FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  onQuestionMoved(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                  questions: FlatModels.QuestionEntityModels.QuestionEntityType[]) {
    this.slideAuthorFacade.requestRearrangeQuestions(structuredClone(page), questions).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  removeQuestion(question: FlatModels.QuestionEntityModels.QuestionEntityType) {
    this.#handled = true;
    if (!question) {
      return;
    }
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.question_remove'),
      data: {
        label: 'global.question_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        // Request Remove Question
        this.slideAuthorFacade.requestRemoveQuestion(question, 'history.question_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
  
  //</editor-fold>
}
